<template>
  <HeadlessPopover as="div" class="relative ml-6">
    <HeadlessPopoverButton
      ref="popperTargetRef"
      href="#"
      class="text-background-fg/50 relative ml-5 flex flex-shrink-0 items-center justify-center rounded-full bg-white p-1 hover:text-opacity-80 focus:outline-none"
    >
      <IconHeroiconsSolidVideoCamera />
    </HeadlessPopoverButton>

    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <HeadlessPopoverPanel
        ref="scrollContainerRef"
        class="ring-background-fg bg-background z-2 absolute right-0 mt-2 origin-top-right overflow-auto rounded-md p-4 shadow-lg ring-1 ring-opacity-5 focus:outline-none"
      >
        <!-- With "All sites" selected useCameras() returns every camera, which
             is far too many tabs — so pick a site first, then its cameras. -->
        <BaseInputSelect
          v-if="cameraSites.length > 1"
          v-model="selectedSiteId"
          :options="siteOptions"
          class="mb-3 w-64"
          placeholder="Select a site"
        />

        <!-- Thin tab per camera (of the selected site), ordered entry-first,
             then one per storage kiosk without a camera (the pedestrian gate). -->
        <div
          v-if="tabs.length > 0"
          class="border-background-fg/10 mb-3 flex gap-1 overflow-x-auto border-b"
        >
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="-mb-px whitespace-nowrap border-b-2 px-3 py-1.5 text-sm transition-colors"
            :class="
              tab.id === activeTab?.id
                ? 'border-primary text-primary font-medium'
                : 'text-background-fg/60 hover:text-background-fg border-transparent'
            "
            @click="activeTabId = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>

        <CameraStreamCard
          v-if="activeTab?.camera"
          :camera="activeTab.camera"
          :kiosk-lane="kioskLaneFor(activeTab.camera.id)"
        />
        <KioskViewFrame v-else-if="activeTab?.lane" :lane="activeTab.lane" />

        <p
          v-else-if="cameras && cameras.length === 0"
          class="text-background-fg/60 py-6 text-sm"
        >
          No cameras at this site
        </p>
      </HeadlessPopoverPanel>
    </Transition>
  </HeadlessPopover>
</template>

<script setup lang="ts">
import { type Camera } from "@/services/cameras/listCameras";
import { useStorageCompound } from "@/services/storage/getStorageCompound";
import { type StorageLane } from "@/services/storage/storage";

const { data: cameras } = useCameras();
const { data: sites } = useSites();

const siteNameById = computed(() => {
  const map = new Map<string, string>();
  sites.value?.forEach((s) => map.set(s.id, s.name));
  return map;
});

// Distinct sites that have an ANPR camera, by name — only those go in the
// dropdown (a site with no ANPR camera has nothing to view here).
const cameraSites = computed(() => {
  const seen = new Set<string>();
  const out: { id: string; name: string }[] = [];
  for (const camera of cameras.value ?? []) {
    if (camera.anprEnabled && camera.siteId && !seen.has(camera.siteId)) {
      seen.add(camera.siteId);
      out.push({
        id: camera.siteId,
        name: siteNameById.value.get(camera.siteId) ?? "Unknown site",
      });
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
});
const siteOptions = computed(() =>
  cameraSites.value.map((s) => ({ value: s.id, label: s.name })),
);

const selectedSiteId = ref<string | null>(null);
watch(
  cameraSites,
  (list) => {
    if (list.length && !list.some((s) => s.id === selectedSiteId.value)) {
      selectedSiteId.value = list[0].id;
    }
  },
  { immediate: true },
);

// Cameras of the selected site (or all when there's only one site in play).
const siteCameras = computed(() => {
  const list = cameras.value ?? [];
  if (cameraSites.value.length <= 1) return list;
  return list.filter((c) => c.siteId === selectedSiteId.value);
});

// A site with a storage compound also has gate kiosks: each vehicle lane's
// kiosk shows beside its camera, a pedestrian kiosk gets its own tab.
const compoundSiteId = computed(() => {
  const siteId = selectedSiteId.value ?? siteCameras.value[0]?.siteId ?? null;
  return sites.value?.find((s) => s.id === siteId)?.hasStorageCompound
    ? siteId
    : null;
});
const { data: compound } = useStorageCompound(compoundSiteId);
const lanes = computed(() =>
  compoundSiteId.value && compound.value?.siteId === compoundSiteId.value
    ? compound.value.lanes
    : [],
);
const kioskLaneFor = (cameraId: string) =>
  lanes.value.find((lane) => lane.cameraId === cameraId) ?? null;

type Tab = {
  id: string;
  label: string;
  camera?: Camera;
  lane?: StorageLane;
};
const tabs = computed<Tab[]>(() => [
  ...siteCameras.value.map((camera) => ({
    id: camera.id,
    label: camera.displayName,
    camera,
  })),
  ...lanes.value
    .filter((lane) => !lane.cameraId)
    .map((lane) => ({ id: lane.id, label: `${lane.name} kiosk`, lane })),
]);

// Default to the site's first camera (entry, per the API's entry-first order);
// fall back to the first if the selected one is no longer present.
const activeTabId = ref<string | null>(null);
const activeTab = computed(() => {
  const list = tabs.value;
  return list.find(({ id }) => id === activeTabId.value) ?? list[0] ?? null;
});
// Switching site resets the tab selection to that site's first camera.
watch(selectedSiteId, () => {
  activeTabId.value = null;
});
</script>
